import { useState } from 'react'
import { PenLine, Clock, XCircle, Archive, RotateCcw } from 'lucide-react'
import DraftCard from './DraftCard'
import DraftEditor from './DraftEditor'
import EmailEntry from './EmailEntry'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { ThreadDetail } from '@/pages/Inbox/Index'

interface ThreadDetailProps {
  thread: ThreadDetail
  filter: string
  attention?: string | null
  search?: string
  cursor?: string | null
  onUpdateStatus: (status: string) => void
}

// Reusable alert banner for thread-level warnings
function AlertBanner({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
      <Icon className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-medium text-amber-800">{title}</p>
        <p className="text-xs text-amber-700 mt-0.5">{description}</p>
      </div>
    </div>
  )
}

// Escalation banner component
function EscalationBanner({ thread }: { thread: ThreadDetail }) {
  const { latest_agent_session, review_reasons } = thread

  const isEscalated = latest_agent_session?.status === 'escalated'
  const isFailed = latest_agent_session?.status === 'failed'
  const hasIngestionFailed = review_reasons.includes('ingestion_failed')

  if (!isEscalated && !isFailed && !hasIngestionFailed) return null

  return (
    <div className="space-y-2">
      {isEscalated && latest_agent_session.escalation_reason && (
        <AlertBanner icon={Clock} title="AI could not process this thread" description={latest_agent_session.escalation_reason} />
      )}
      {isFailed && (
        <AlertBanner icon={XCircle} title="Processing failed" description="Please handle this thread manually." />
      )}
      {hasIngestionFailed && (
        <AlertBanner icon={XCircle} title="Email ingestion failed" description="One or more emails in this thread could not be processed." />
      )}
    </div>
  )
}

// Thread action buttons based on current status
function ThreadActions({ status, onUpdateStatus }: { status: string; onUpdateStatus: (status: string) => void }) {
  const buttons = []

  if (status === 'open') {
    buttons.push(
      <Button
        key="pending"
        variant="outline"
        size="sm"
        className="h-7 text-xs cursor-pointer"
        onClick={() => onUpdateStatus('pending')}
      >
        <Clock className="h-3 w-3 mr-1.5" />
        Mark Pending
      </Button>,
      <Button
        key="close"
        variant="outline"
        size="sm"
        className="h-7 text-xs cursor-pointer"
        onClick={() => onUpdateStatus('closed')}
      >
        <Archive className="h-3 w-3 mr-1.5" />
        Close
      </Button>
    )
  } else if (status === 'pending') {
    buttons.push(
      <Button
        key="reopen"
        variant="outline"
        size="sm"
        className="h-7 text-xs cursor-pointer"
        onClick={() => onUpdateStatus('open')}
      >
        <RotateCcw className="h-3 w-3 mr-1.5" />
        Reopen
      </Button>,
      <Button
        key="close"
        variant="outline"
        size="sm"
        className="h-7 text-xs cursor-pointer"
        onClick={() => onUpdateStatus('closed')}
      >
        <Archive className="h-3 w-3 mr-1.5" />
        Close
      </Button>
    )
  } else if (status === 'closed') {
    buttons.push(
      <Button
        key="reopen"
        variant="outline"
        size="sm"
        className="h-7 text-xs cursor-pointer"
        onClick={() => onUpdateStatus('open')}
      >
        <RotateCcw className="h-3 w-3 mr-1.5" />
        Reopen
      </Button>
    )
  }

  return <div className="flex items-center gap-2">{buttons}</div>
}

export default function ThreadDetail({ thread, filter, attention, search, cursor, onUpdateStatus }: ThreadDetailProps) {
  const [composing, setComposing] = useState(false)

  // Find the latest email for composing replies — prefer inbound, fall back to outbound
  const latestInboundEntry = [...thread.timeline]
    .reverse()
    .find(e => e.type === 'inbound')

  const latestEntry = latestInboundEntry || [...thread.timeline].reverse()[0]

  const latestEmail = latestInboundEntry ? {
    id: latestInboundEntry.id,
    from: latestInboundEntry.from || '',
    subject: latestInboundEntry.subject || '',
  } : null

  // Determine reply-to address: reply to the sender of latest inbound, or the "to" of latest outbound
  const replyTo = latestInboundEntry?.from || latestEntry?.to || ''

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header with subject and actions */}
      <div className="flex h-14 items-center justify-between border-b px-6">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-sm font-semibold text-foreground truncate">
            {thread.subject || '(no subject)'}
          </h2>
          <Badge variant="secondary" size="sm" className="shrink-0">
            {thread.emails_count} {thread.emails_count === 1 ? 'email' : 'emails'}
          </Badge>
        </div>
        <ThreadActions status={thread.status} onUpdateStatus={onUpdateStatus} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Escalation Banner */}
        <EscalationBanner thread={thread} />

        {/* Parent-anchored timeline — inbound backbone with outbound entries nested inline */}
        <div className="space-y-4">
          {thread.timeline.map((entry, index) => {
            if (entry.type === 'outbound' && entry.status === 'sent') {
              return <EmailEntry key={entry.id} entry={entry} isLatest={index === thread.timeline.length - 1} />
            }
            if (entry.type === 'outbound') {
              // Draft, sending, or send_failed — render via DraftCard inline
              return (
                <DraftCard
                  key={entry.id}
                  draft={{
                    id: entry.id,
                    to: entry.to || '',
                    cc: entry.cc || null,
                    bcc: entry.bcc || null,
                    subject: entry.subject || '',
                    body: entry.body || '',
                    body_html: entry.body_html || null,
                    original_body: entry.original_body || null,
                    status: entry.status,
                    sent_at: entry.sent_at || null,
                    sent_by_id: entry.sent_by_id || null,
                    created_at: entry.created_at || '',
                    created_by_id: entry.created_by_id || null,
                    agent_session_id: entry.agent_session_id || null,
                    send_error: entry.send_error || null,
                    attachments: entry.attachments?.map(a => ({ ...a, size: a.size })),
                  }}
                  filter={filter}
                  attention={attention}
                  search={search}
                  cursor={cursor}
                />
              )
            }
            // Inbound email
            return (
              <EmailEntry
                key={entry.id}
                entry={entry}
                isLatest={index === thread.timeline.length - 1}
              />
            )
          })}
        </div>

        {/* Compose Reply */}
        {composing ? (
          <DraftEditor
            emailThreadId={thread.id}
            replyToEmailId={latestEmail?.id}
            to={replyTo}
            filter={filter}
            attention={attention}
            search={search}
            cursor={cursor}
            onClose={() => setComposing(false)}
          />
        ) : (
          <div className="flex justify-end pt-0">
            <Button
              size="sm"
              className="cursor-pointer"
              onClick={() => setComposing(true)}
            >
              <PenLine className="size-3.5 mr-1.5" />
              Compose Reply
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
